import moment from "moment";
import * as React from "react";
import { useTable } from "react-table";

import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { DateCell } from "src/components/Displays";
import { TextButton } from "src/components/Forms/Base/Buttons";
import NonConnectedDatePicker from "src/components/Forms/Base/Old/NonConnected/NonConnectedDatePicker";
import { StatusLabel } from "src/components/Lists/Base/CompleteList/styles";
import { axiosInstance } from "src/store/base/store/axios";
import { getInvoiceIdPreview } from "src/store/billectaInvoicing";
import {
  billectaStateMap,
  DELIVERY_METHOD_MAPPER_EXCL_PRICES,
  invoiceDetailUrl,
} from "src/store/billectaInvoicing/utils";
import { addToast, TOAST_TYPES } from "src/store/toasts";
import { Field } from "../Table";
import * as SC from "../Table/styles";

const getUrl = ({ creditorId, fromDate, toDate }) =>
  `/accounting/gateways/invoices/closed/${creditorId}/?sortingfield=InvoiceDate&asc=false&from=${fromDate}&to=${toDate}`;

export default function ClosedInvoicesTable({ creditorId }) {
  const dispatch = useDispatch();
  const { push } = useHistory();

  // State management
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [fromDate, setFromDate] = React.useState(
    moment().subtract(3, "months").toDate()
  );
  const [toDate, setToDate] = React.useState(moment().toDate());
  const [searchTerm, setSearchTerm] = React.useState("");
  const [currentPage, setCurrentPage] = React.useState(0);
  const pageSize = 500;

  const handleDownloadInvoice = (invoice) => {
    dispatch(
      addToast({
        type: TOAST_TYPES.INFO,
        title: "Hämtar faktura...",
      })
    );
    dispatch(
      getInvoiceIdPreview({
        invoiceId: invoice?.ActionPublicId,
        download: true,
      })
    );
  };

  const handleRowClicked = (row) => {
    push(
      invoiceDetailUrl({
        creditorId,
        invoiceId: row.original.ActionPublicId,
      })
    );
  };

  const columns = React.useMemo(() => {
    const cols = [
      {
        Header: "Status",
        accessor: "Stage",
        Cell: (props) => {
          if (props.row?.original?.IsDisputed) {
            return (
              <StatusLabel state={6}>
                {billectaStateMap[props.value]} (Bestriden)
              </StatusLabel>
            );
          }

          if (props.row?.original?.ActionType === "CreditInvoiceAction") {
            if (props.row?.original?.AttestedDate == null) {
              return (
                <StatusLabel state={"Kreditfaktura"}>
                  Kreditfaktura (Ej attesterad)
                </StatusLabel>
              );
            }
            return (
              <StatusLabel state={"Kreditfaktura"}>Kreditfaktura</StatusLabel>
            );
          }
          return (
            <StatusLabel state={billectaStateMap[props.value]}>
              {billectaStateMap[props.value]}
            </StatusLabel>
          );
        },
      },
      {
        Header: "Fakturanr.",
        accessor: "InvoiceNumber",
        Cell: (props) => {
          return <Field>{props.value}</Field>;
        },
      },

      {
        Header: "Hyresgäst",
        accessor: "DebtorName",
        Cell: (props) => {
          return <Field>{props.value}</Field>;
        },
      },
      {
        Header: "Originalbelopp (SEK)",
        accessor: "total",
        Cell: (props) => {
          return (
            <Field>
              {(props.row.original.InvoicedAmount.Value / 100).toLocaleString(
                "sv"
              )}
            </Field>
          );
        },
      },
      {
        Header: "Kvarvarande originalbelopp (SEK)",
        accessor: "totalLeft",
        Cell: (props) => {
          return (
            <Field>
              {(props.row.original.CurrentAmount.Value / 100).toLocaleString(
                "sv"
              )}
            </Field>
          );
        },
      },
      {
        Header: "Leveransmetod",
        accessor: "DeliveryMethod",
        Cell: (props) => {
          return (
            <Field>{DELIVERY_METHOD_MAPPER_EXCL_PRICES[props.value]}</Field>
          );
        },
      },
      {
        Header: "Attesterad",
        accessor: "AttestedDate",
        Cell: (props) => {
          return <Field>{props.value ? "Ja" : "Nej"}</Field>;
        },
      },
      {
        Header: "Fakturadatum",
        accessor: "InvoiceDate",
        Cell: (props) => {
          return <DateCell date={props.value} />;
        },
      },
      {
        Header: "Förfallodatum",
        accessor: "DueDate",
        Cell: (props) => {
          return <DateCell date={props.value} />;
        },
      },

      {
        Header: "Skapad",
        accessor: "Created",
        Cell: (props) => {
          return <DateCell date={props.value} />;
        },
      },
      {
        Header: "Betalningsinfo",
        id: "paid_relation",
        Cell: ({ row }) => {
          const closedDate = moment(row.original.ClosedDate);
          const dueDate = moment(row.original.DueDate);
          const now = moment();
          const attested = !!row.original.AttestedDate;
          const isCredit = row.original.ActionType === "CreditInvoiceAction";

          if (!attested || isCredit) return <Field> - </Field>;
          // not paid, late
          if (!closedDate.isValid() && now.isAfter(dueDate)) {
            const lateDays = now.diff(dueDate, "days");

            return (
              <StatusLabel state={6}>
                Ej betalad, försenad med {lateDays} dag
                {lateDays > 1 ? "ar" : ""}
              </StatusLabel>
            );
          }

          if (closedDate.isSameOrBefore(dueDate)) {
            return (
              <StatusLabel state={"Slutbetald"}>Betalad i tid</StatusLabel>
            );
          }

          if (closedDate.isAfter(dueDate)) {
            const latePaymentDays = closedDate.diff(dueDate, "days");
            return (
              <StatusLabel state={"Påminnelse"}>
                Betalad {latePaymentDays} dag
                {latePaymentDays > 1 ? "ar" : ""} för sent
              </StatusLabel>
            );
          }

          return <StatusLabel state={9}>Inväntar betalning</StatusLabel>;
        },
      },
    ];

    if (handleDownloadInvoice) {
      cols.push({
        Header: "Ladda ner",
        Cell: (props) => {
          return (
            <Field style={{ textAlign: "center" }}>
              <TextButton
                title="Ladda ner"
                iconType="download"
                iconPlacement="right"
                clicked={() => handleDownloadInvoice(props.cell.row.original)}
              />
            </Field>
          );
        },
      });
    }

    return cols;
  }, []);

  // Filter data based on search term
  const filteredData = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return data;
    }

    const searchLower = searchTerm.toLowerCase().trim();
    return data.filter((item) => {
      // Search across relevant text fields
      const searchableFields = [
        item.DebtorName,
        item.InvoiceNumber,
        item.OCR,
        item.YourReference,
        item.CreditorName,
        item.ActionPublicId,
      ];

      return searchableFields.some(
        (field) => field && field.toString().toLowerCase().includes(searchLower)
      );
    });
  }, [data, searchTerm]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [filteredData]);

  const totalPages = Math.ceil(filteredData.length / pageSize);
  const paginatedData = React.useMemo(() => {
    const start = currentPage * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage]);

  const toBeforeFrom = React.useMemo(() => {
    if (!fromDate || !toDate) return false;
    return moment(toDate).isBefore(moment(fromDate));
  }, [fromDate, toDate]);

  const intervalExceeded = React.useMemo(() => {
    if (!fromDate || !toDate || toBeforeFrom) return false;
    return moment(toDate).diff(moment(fromDate), "months", true) > 5;
  }, [fromDate, toDate, toBeforeFrom]);

  // Data fetching function
  const fetchData = React.useCallback(async () => {
    if (!creditorId) return;

    setLoading(true);
    try {
      const url = getUrl({
        creditorId,
        fromDate: moment(fromDate).format("YYYY-MM-DD"),
        toDate: moment(toDate).format("YYYY-MM-DD"),
      });

      const response = await axiosInstance.get(url);
      let newData = response.data || [];

      // Sort data by InvoiceDate descending (latest first)
      newData = newData.sort((a, b) => {
        const dateA = moment(a.InvoiceDate);
        const dateB = moment(b.InvoiceDate);
        return dateB.diff(dateA); // descending order
      });

      setData(newData);
    } catch (error) {
      console.error("Error fetching invoices:", error);
      dispatch(
        addToast({
          type: TOAST_TYPES.ERROR,
          title: "Fel vid hämtning av fakturor",
          message: error.message,
        })
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [creditorId, fromDate, toDate, dispatch]);

  const fetchAll = React.useCallback(async () => {
    if (!creditorId) return;

    setLoading(true);
    try {
      const allData = [];
      const now = moment();
      const twoYearsAgo = moment().subtract(2, "years");
      let chunkTo = now.clone();

      while (true) {
        const chunkFrom = chunkTo.clone().subtract(3, "months");
        const url = getUrl({
          creditorId,
          fromDate: chunkFrom.format("YYYY-MM-DD"),
          toDate: chunkTo.format("YYYY-MM-DD"),
        });

        const response = await axiosInstance.get(url);
        const chunkData = response.data || [];
        allData.push(...chunkData);

        const passedMinimum = chunkFrom.isSameOrBefore(twoYearsAgo);
        if (passedMinimum && chunkData.length === 0) break;

        chunkTo = chunkFrom.clone();
      }

      const uniqueData = Array.from(
        new Map(allData.map((inv) => [inv.ActionPublicId, inv])).values()
      );

      uniqueData.sort((a, b) => moment(b.InvoiceDate).diff(moment(a.InvoiceDate)));

      setData(uniqueData);
    } catch (error) {
      console.error("Error fetching all invoices:", error);
      dispatch(
        addToast({
          type: TOAST_TYPES.ERROR,
          title: "Fel vid hämtning av fakturor",
          message: error.message,
        })
      );
    } finally {
      setLoading(false);
    }
  }, [creditorId, dispatch]);

  // Reset and fetch data when dates change
  const handleFromDateChange = (newFromDate) => {
    setFromDate(newFromDate);
  };

  const handleToDateChange = (newToDate) => {
    setToDate(newToDate);
  };

  // Initial data fetch only
  React.useEffect(() => {
    fetchData();
  }, [creditorId]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable({
      columns,
      data: paginatedData,
    });

  // Handle row click
  const handleTableRowClick = (row) => {
    handleRowClicked(row);
  };

  return (
    <>
      {/* CSS for loading spinner animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>

      {/* Date Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          flexWrap: "wrap",
          gap: "16px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 8 }}>Från datum:</div>
          <NonConnectedDatePicker
            inputExtraStyle={{ maxWidth: 160 }}
            id="billecta_date_from"
            value={fromDate}
            onChange={(date) => {
              handleFromDateChange(date);
            }}
            clearable={false}
            noMargin
            maxDate={toDate}
          />
        </div>

        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 8 }}>Till datum:</div>
          <NonConnectedDatePicker
            inputExtraStyle={{ maxWidth: 160 }}
            id="billecta_date_to"
            value={toDate}
            onChange={(date) => {
              handleToDateChange(date);
            }}
            clearable={false}
            noMargin
            maxDate={moment().toDate()}
          />
        </div>

        <button
          onClick={fetchData}
          disabled={loading || intervalExceeded || toBeforeFrom}
          style={{
            padding: "8px 16px",
            border: "none",
            borderRadius: "4px",
            background: loading || intervalExceeded || toBeforeFrom ? "#a0c4e8" : "#2196f3",
            color: "#fff",
            cursor: loading || intervalExceeded || toBeforeFrom ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Uppdatera sökning
        </button>

        <button
          onClick={fetchAll}
          disabled={loading}
          style={{
            padding: "8px 16px",
            border: "1px solid #2196f3",
            borderRadius: "4px",
            background: loading ? "#f5f5f5" : "#fff",
            color: loading ? "#a0c4e8" : "#2196f3",
            cursor: loading ? "not-allowed" : "pointer",
            fontSize: "14px",
            fontWeight: 500,
          }}
        >
          Hämta alla
        </button>

        {toBeforeFrom && (
          <div style={{ color: "#d32f2f", fontSize: "13px" }}>
            Till-datum måste vara efter från-datum
          </div>
        )}

        {intervalExceeded && (
          <div style={{ color: "#d32f2f", fontSize: "13px" }}>
            Max 5 månaders intervall mellan datumen
          </div>
        )}

        {/* Search Field */}
        <div style={{ display: "flex", alignItems: "center" }}>
          <div style={{ marginRight: 8 }}>Sök:</div>
          <input
            type="text"
            placeholder="Sök på namn, fakturanummer, OCR, referens..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: "8px 12px",
              border: "1px solid #ddd",
              borderRadius: "4px",
              minWidth: "300px",
              fontSize: "14px",
            }}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                marginLeft: "8px",
                padding: "8px 12px",
                border: "1px solid #ddd",
                borderRadius: "4px",
                background: "#f5f5f5",
                cursor: "pointer",
                fontSize: "12px",
              }}
            >
              Rensa
            </button>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-500">
        <p>
          Tips: Avgränsa datumen för att snabba upp hämtning av avier &
          fakturor. Ju kortare intervall desto snabbare hämtning.
        </p>
      </div>

      <SC.TableWrapper>
        {/* Loading indicator for all fetch operations */}
        {loading && (
          <div
            style={{
              padding: "15px 20px",
              backgroundColor: "#e3f2fd",
              borderLeft: "4px solid #2196f3",
              marginBottom: "10px",
              fontSize: "14px",
              color: "#1565c0",
              display: "flex",
              alignItems: "center",
              gap: "10px",
            }}
          >
            <div
              style={{
                width: "16px",
                height: "16px",
                border: "2px solid #bbdefb",
                borderTop: "2px solid #2196f3",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
              }}
            />
            {data.length === 0
              ? "Laddar fakturor..."
              : "Uppdaterar fakturor..."}
          </div>
        )}

        {/* Search results info */}
        {searchTerm && !loading && (
          <div
            style={{
              padding: "10px 20px",
              backgroundColor: "#f8f9fa",
              borderBottom: "1px solid #dee2e6",
              fontSize: "14px",
              color: "#6c757d",
            }}
          >
            Visar {filteredData.length} av {data.length} fakturor som matchar "
            {searchTerm}"
          </div>
        )}

        <SC.InnerTableWrapper style={{ position: "relative" }}>
          {/* Loading overlay for table */}
          {loading && data.length > 0 && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.7)",
                zIndex: 10,
                pointerEvents: "none",
              }}
            />
          )}
          <SC.Table {...getTableProps()}>
            <thead>
              {headerGroups.map((headerGroup) => (
                <tr {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column) => (
                    <th
                      {...column.getHeaderProps()}
                      style={{
                        borderBottom: "2px solid #ddd",
                        padding: "12px",
                        textAlign: "left",
                        fontWeight: "bold",
                        backgroundColor: "#f5f5f5",
                      }}
                    >
                      {column.render("Header")}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody {...getTableBodyProps()}>
              {rows.map((row) => {
                prepareRow(row);
                return (
                  <tr
                    {...row.getRowProps()}
                    onClick={() => handleTableRowClick(row)}
                    style={{
                      cursor: "pointer",
                    }}
                    onMouseEnter={(e) =>
                      (e.target.parentElement.style.backgroundColor = "#f9f9f9")
                    }
                    onMouseLeave={(e) =>
                      (e.target.parentElement.style.backgroundColor = "")
                    }
                  >
                    {row.cells.map((cell) => (
                      <td
                        {...cell.getCellProps()}
                        style={{
                          padding: "12px",
                          borderBottom: "1px solid #ddd",
                        }}
                      >
                        {cell.render("Cell")}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </SC.Table>

          {/* Pagination & Data summary */}
          {(data.length > 0 || loading) && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 20px",
                color: "#666",
                borderTop: "1px solid #eee",
              }}
            >
              <div>
                {loading && data.length === 0
                  ? "Laddar fakturor..."
                  : loading && data.length > 0
                  ? `Uppdaterar... (${data.length} fakturor)`
                  : `Visar ${currentPage * pageSize + 1}–${Math.min(
                      (currentPage + 1) * pageSize,
                      filteredData.length
                    )} av ${filteredData.length} fakturor`}
              </div>

              {totalPages > 1 && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <button
                    onClick={() => setCurrentPage((p) => p - 1)}
                    disabled={currentPage === 0}
                    style={{
                      padding: "4px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      background: currentPage === 0 ? "#f5f5f5" : "#fff",
                      cursor: currentPage === 0 ? "not-allowed" : "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Föregående
                  </button>
                  <span style={{ fontSize: "13px" }}>
                    Sida {currentPage + 1} av {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage((p) => p + 1)}
                    disabled={currentPage >= totalPages - 1}
                    style={{
                      padding: "4px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      background: currentPage >= totalPages - 1 ? "#f5f5f5" : "#fff",
                      cursor: currentPage >= totalPages - 1 ? "not-allowed" : "pointer",
                      fontSize: "13px",
                    }}
                  >
                    Nästa
                  </button>
                </div>
              )}
            </div>
          )}
        </SC.InnerTableWrapper>
      </SC.TableWrapper>
    </>
  );
}
